import React, { useState, useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import useScoreEntry, { TENS_SCORES } from './hooks/useScoreEntry';
import './ScoreInputModal.css';

const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// The landscape layout styles its keys inline (no stylesheet class), so the
// shared shape lives here rather than being repeated per key.
const LANDSCAPE_KEY = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: '1px solid rgba(0,0,0,0.08)',
  background: 'rgba(0,0,0,0.03)',
  color: '#1e293b',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  WebkitTapHighlightColor: 'transparent',
  padding: 0,
  margin: '0 auto',
  fontFamily: 'inherit',
};

const useIsLandscape = () => {
  const [landscape, setLandscape] = useState(
    () => window.innerWidth > window.innerHeight
  );
  useEffect(() => {
    const handler = () => setLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return landscape;
};

const ScoreInputModal = ({
  isOpen,
  onClose,
  onScoreSelect,
  holeData,
  playerName,
  holeNumber,
}) => {
  const isLandscape = useIsLandscape();
  // Every key saves on one tap; 10–20 sit behind "10 or more". See
  // useScoreEntry — this pad and ScoreInputPad must behave identically.
  const { showTens, submit, openTens, closeTens } =
    useScoreEntry(onScoreSelect, `${playerName}-${holeNumber}`);

  if (!isOpen) return null;

  if (isLandscape) {
    return (
      <div
        className="modal-overlay"
        onClick={onClose}
        style={{ alignItems: 'flex-start', justifyContent: 'center', paddingTop: 8 }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: 'rgba(255,255,255,0.93)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            borderRadius: 16,
            boxShadow: '0 16px 32px rgba(0,0,0,0.22)',
            padding: '22px 24px',
            width: '92%',
            maxWidth: 600,
          }}
        >
          {/* Player / hole info */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, marginBottom: 8,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
              {playerName}
            </span>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
              Hole {holeNumber} · Par {holeData?.par} · SI {holeData?.strokeIndex}
            </span>
          </div>

          {/* Every key saves on one tap; 10–20 sit behind "10 or more", since
              the pad used to stop at 12 and a bad stroke-play hole simply
              couldn't be recorded. */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 10,
            marginBottom: 14,
          }}>
            {(showTens ? TENS_SCORES : DIGITS).map(n => (
              <button key={n} onClick={() => submit(n)} style={LANDSCAPE_KEY}>
                {n}
              </button>
            ))}
            {showTens ? (
              <button
                onClick={closeTens}
                aria-label="Back to single figures"
                style={{ ...LANDSCAPE_KEY, color: '#64748b' }}
              ><FiArrowLeft size={16} /></button>
            ) : (
              <button
                onClick={openTens}
                style={{
                  ...LANDSCAPE_KEY,
                  gridColumn: '1 / -1',
                  width: '100%',
                  height: 34,
                  borderRadius: 17,
                  fontSize: 13, fontWeight: 700,
                  color: '#475569',
                }}
              >10 or more</button>
            )}
          </div>

          {/* NS + Clear + Done */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => onScoreSelect('CLEAR')}
              style={{
                flex: 1, height: 32, borderRadius: 16,
                border: '1px solid rgba(185,28,28,0.25)',
                background: 'rgba(185,28,28,0.06)',
                color: '#991b1b', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit',
              }}
            >Clear</button>
            <button
              onClick={() => onScoreSelect('NS')}
              style={{
                flex: 1, height: 32, borderRadius: 16,
                border: '1px solid rgba(0,0,0,0.08)',
                background: 'rgba(0,0,0,0.03)',
                color: '#6b7280', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit',
              }}
            >NS</button>
            <button
              onClick={onClose}
              style={{
                flex: 1, height: 32, borderRadius: 16,
                border: 'none',
                background: 'linear-gradient(135deg, #2a7cff, #4aa3ff)',
                color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit',
              }}
            >Done</button>
          </div>
        </div>
      </div>
    );
  }

  // Portrait — unchanged CSS class layout
  return (
    <div className="modal-overlay score-input-modal" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{playerName}</h3>
          <p>Hole {holeNumber} &bull; Par {holeData?.par} &bull; SI {holeData?.strokeIndex}</p>
        </div>
        <div className="modal-divider"></div>
        <div className="keypad">
          {showTens ? (
            <>
              {TENS_SCORES.map(n => (
                <button key={n} className="keypad-button keypad-button--tens" onClick={() => submit(n)}>{n}</button>
              ))}
              <button
                className="keypad-button keypad-button--util"
                onClick={closeTens}
                aria-label="Back to single figures"
              ><FiArrowLeft size={17} /></button>
            </>
          ) : (
            <>
              {DIGITS.map(n => (
                <button key={n} className="keypad-button" onClick={() => submit(n)}>{n}</button>
              ))}
              <button className="keypad-button keypad-button--tens-open" onClick={openTens}>
                10 or more
              </button>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button className="keypad-action-button clear-button" onClick={() => onScoreSelect('CLEAR')}>
            Clear
          </button>
          <button className="keypad-action-button ns-button" onClick={() => onScoreSelect('NS')}>
            NS
          </button>
          <button className="keypad-action-button done-button" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoreInputModal;
